import type Highcharts from "highcharts";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../../api/useSetups";
import t from "../../../../i18n";
import { color } from "@ui";
import type { FailureImpactMetadata } from "../../Chart/PointMetadata";
import seriesColors from "../../Chart/series/seriesColors";
import getQuantityOffset from "../../../../api/getQuantityOffset";
import getQuantitySigma from "../../../../api/getQuantitySigma";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import type { Quantity } from "../../../../api/useQuantities";
import { isAnalysisQuantityValid } from "../../metrology";
import { computeQuantityGap } from "diagnostics-domain/quantity/gap";
export const QUANTITY_GAP_SERIE_ID = "gap-serie";

export default function getQuantityGapsSerie(
  quantities: Quantity[],
  analysis: ActiveAnalysis,
  normalized: boolean,
  setup: Setup | null,
  _metrologyOverrides: MetrologyOverrides,
): Highcharts.SeriesColumnOptions {
  const data: Highcharts.PointOptionsObject[] = quantities.map(quantity => {
    const quantityValue = analysis.quantityValues[quantity.id];
    if (!quantityValue) {
      const custom: FailureImpactMetadata = {
        human: t("diagnosis.charts.symptoms.no_symptoms"),
        failureStatus: null,
        confidenceIndicator: null,
        isManual: null,
        isPromoted: null,
      };

      return {
        name: quantity.name,
        y: 0,
        custom,
        dataLabels: {
          enabled: true,
          align: "center",
          color: color.oxygen400,
          formatter: () => "\u2716",
        },
      };
    }

    const offset = getQuantityOffset(setup, quantity.id);
    const valid = isAnalysisQuantityValid(analysis, quantity);

    const gap = valid
      ? computeQuantityGap(quantityValue.measured, quantityValue.simulated, offset)
      : null;
    const value =
      gap === null ? null : normalized ? gap / (getQuantitySigma(setup, quantity.id) ?? 1) : gap;
    const custom: FailureImpactMetadata = {
      human:
        gap === null
          ? t("diagnosis.charts.symptoms.no_symptoms")
          : {
              value: gap,
              unit: quantity.unit,
              precision: 2,
              sign: true,
            },
      failureStatus: null,
      confidenceIndicator: null,
      isManual: null,
      isPromoted: null,
    };

    return {
      name: quantity.name,
      y: value ?? 0,
      custom,
      dataLabels:
        value === null
          ? {
              enabled: true,
              align: "center",
              color: color.oxygen400,
              formatter: () => "\u2716",
            }
          : undefined,
    };
  });

  return {
    name: t("diagnosis.charts.symptoms.legend.symptoms"),
    color: seriesColors.symptom,
    borderColor: seriesColors.symptom,
    id: QUANTITY_GAP_SERIE_ID,
    stack: "2",
    data,
    type: "column",
    replicateInNavigator: false,
    showInLegend: true,
    legendIndex: 0,
  };
}
